import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  X,
  HelpCircle,
  SkipForward,
  ChevronLeft,
  Zap,
  Loader2,
  Globe2,
  ExternalLink,
  Sparkles,
  Bot,
  RefreshCw,
} from "lucide-react";
import {
  getPendingSubmissions,
  verifySubmission,
  setAtomVerdict,
  regroundSubmission,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { TranslateButton } from "../components/TranslateButton";

const SHORTCUTS = {
  ArrowRight: "confirm",
  ArrowLeft: "reject",
  ArrowUp: "opinion",
  ArrowDown: "skip",
  KeyG: "reground",
  KeyA: "atom-confirm-all",
  KeyR: "atom-reject-all",
};

function VerdictBadge({ v }) {
  const map = {
    true:      { label: "AI: TRUE",      cls: "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" },
    false:     { label: "AI: FALSE",     cls: "border-rose-500/50 text-rose-300 bg-rose-500/10" },
    uncertain: { label: "AI: UNCERTAIN", cls: "border-zinc-700 text-zinc-400" },
  };
  const m = map[v] || map.uncertain;
  return (
    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${m.cls}`}>
      {m.label}
    </span>
  );
}

function KindPill({ kind }) {
  const map = {
    verifiable: { label: "verifiable", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
    unprovable: { label: "needs proof", cls: "border-amber-500/40 text-amber-300 bg-amber-500/5" },
    opinion:    { label: "opinion",    cls: "border-violet-500/40 text-violet-300 bg-violet-500/5" },
    harmful:    { label: "harmful",    cls: "border-rose-500/50 text-rose-300 bg-rose-500/10" },
  };
  const m = map[kind] || map.verifiable;
  return (
    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function VerificationQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [regrounding, setRegrounding] = useState(false);
  const [stats, setStats] = useState({ confirmed: 0, rejected: 0, opinion: 0, skipped: 0, apt_minted: 0 });
  const [activeAtoms, setActiveAtoms] = useState([]);
  const [swipeDir, setSwipeDir] = useState(null);  // "left" | "right" | "up" | null

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPendingSubmissions(50);
      const rows = res.items || res.submissions || [];
      setQueue(rows);
      setIdx(0);
      setActiveAtoms((rows[0] || {}).atoms || []);
    } catch {
      toast.error("Couldn't load queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_architect) return;
    reload();
  }, [user?.is_architect, reload]);

  const current = queue[idx];

  useEffect(() => {
    setActiveAtoms(current?.atoms || []);
  }, [current?.id]);

  const next = () => {
    setSwipeDir(null);
    setIdx((i) => i + 1);
  };

  const handle = useCallback(async (action) => {
    if (!current || busy) return;
    setBusy(true);
    setSwipeDir(action === "confirm" ? "right" : action === "reject" ? "left" : action === "opinion" ? "up" : null);
    try {
      if (action === "skip") {
        toast(`Skipped · ${current.claim.slice(0, 50)}…`);
        setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
      } else {
        const verdict = action;
        const res = await verifySubmission(current.id, verdict);
        const apt = Number(res?.submitter_apt_reward || 0);
        const c = res?.atoms_confirmed ?? 0;
        const t = res?.atoms_total ?? 0;
        if (verdict === "confirmed") {
          toast.success(`✓ ${apt.toFixed(2)} APT minted${t > 0 ? ` · ${c}/${t} atoms` : ""}`);
          setStats((s) => ({ ...s, confirmed: s.confirmed + 1, apt_minted: s.apt_minted + apt }));
        } else if (verdict === "rejected") {
          toast(`✗ Rejected`);
          setStats((s) => ({ ...s, rejected: s.rejected + 1 }));
        } else if (verdict === "opinion") {
          toast(`◇ Marked opinion`);
          setStats((s) => ({ ...s, opinion: s.opinion + 1 }));
        }
      }
      setTimeout(next, 200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Action failed");
      setSwipeDir(null);
    } finally {
      setBusy(false);
    }
  }, [current, busy]);

  const reground = async () => {
    if (!current) return;
    setRegrounding(true);
    try {
      const res = await regroundSubmission(current.id);
      const fresh = res?.triangulation;
      if (fresh) {
        setQueue((q) => q.map((row, i) => i === idx ? { ...row, triangulation: fresh } : row));
      }
      const count = (fresh?.grounding?.sources || []).length;
      toast.success(`Re-grounded · ${count} source${count === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-grounding failed");
    } finally {
      setRegrounding(false);
    }
  };

  const setAtom = async (atomId, verdict) => {
    if (!current) return;
    try {
      const res = await setAtomVerdict(current.id, atomId, verdict);
      setActiveAtoms(res.atoms || []);
      setQueue((q) => q.map((row, i) => i === idx ? { ...row, atoms: res.atoms } : row));
    } catch (err) {
      toast.error("Atom update failed");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const action = SHORTCUTS[e.code];
      if (!action) return;
      e.preventDefault();
      if (action === "reground") reground();
      else if (action === "atom-confirm-all") {
        (activeAtoms || []).filter(a => a.kind === "verifiable").forEach(a => setAtom(a.id, "confirmed"));
      } else if (action === "atom-reject-all") {
        (activeAtoms || []).filter(a => a.kind === "verifiable").forEach(a => setAtom(a.id, "rejected"));
      } else {
        handle(action === "confirm" ? "confirmed" : action === "reject" ? "rejected" : action === "opinion" ? "opinion" : "skip");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handle, activeAtoms, current]);

  if (!user?.is_architect) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center text-zinc-500">
        Architect only.
        <div className="mt-4">
          <Link to="/submit" className="text-amber-300 underline">go to /submit</Link>
        </div>
      </div>
    );
  }

  const remaining = Math.max(0, queue.length - idx);
  const tri = current?.triangulation;
  const sources = tri?.grounding?.sources || [];

  const swipeTransform =
    swipeDir === "right" ? "translateX(120%) rotate(15deg)" :
    swipeDir === "left"  ? "translateX(-120%) rotate(-15deg)" :
    swipeDir === "up"    ? "translateY(-100%) scale(0.9)" :
    "none";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-mono flex items-center gap-2">
            <Zap className="h-3 w-3" /> rapid verification
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif tracking-tight text-zinc-100 mt-1">
            Burn through the queue.
          </h1>
        </div>
        <Link
          to="/submit"
          className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-amber-300 font-mono inline-flex items-center gap-1"
        >
          <ChevronLeft className="h-3 w-3" /> classic view
        </Link>
      </header>

      <div className="border border-zinc-800 bg-black/40 px-3 py-2 grid grid-cols-5 gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
        <div data-testid="queue-stat-remaining"><span className="text-amber-300">{remaining}</span><br/>queue</div>
        <div data-testid="queue-stat-confirmed"><span className="text-emerald-300">{stats.confirmed}</span><br/>confirmed</div>
        <div data-testid="queue-stat-rejected"><span className="text-rose-300">{stats.rejected}</span><br/>rejected</div>
        <div data-testid="queue-stat-opinion"><span className="text-violet-300">{stats.opinion}</span><br/>opinion</div>
        <div data-testid="queue-stat-apt"><span className="text-amber-300">{stats.apt_minted.toFixed(2)}</span><br/>APT</div>
      </div>

      {loading && (
        <div className="text-center text-zinc-500 py-12 text-[12px] font-mono uppercase tracking-[0.3em]">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" />
          loading queue…
        </div>
      )}

      {!loading && !current && (
        <div className="border border-dashed border-emerald-500/30 bg-emerald-500/[0.03] p-8 text-center" data-testid="queue-empty-state">
          <Check className="h-8 w-8 mx-auto mb-3 text-emerald-300/70" />
          <div className="text-zinc-200 text-sm">Queue cleared.</div>
          <button
            onClick={reload}
            className="mt-4 text-[10px] uppercase tracking-[0.25em] text-amber-300 hover:underline font-mono inline-flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" /> refresh
          </button>
        </div>
      )}

      {current && (
        <>
          {/* Swipe card */}
          <article
            key={current.id}
            data-testid={`queue-card-${current.id}`}
            className="border border-zinc-800 bg-black/50 p-5 transition-transform duration-200 ease-out"
            style={{ transform: swipeTransform }}
          >
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 font-mono">
                {current.claim_type}
              </span>
              <span className="text-[10px] font-mono text-zinc-600">#{idx + 1} of {queue.length}</span>
              <span className="ml-auto text-[10px] font-mono text-zinc-600">
                {new Date(current.created_at).toLocaleString()}
              </span>
            </div>

            <p className="font-serif text-zinc-100 text-lg leading-snug mb-3">
              &ldquo;{current.claim}&rdquo;
            </p>
            <TranslateButton
              text={current.claim}
              inline
              testIdPrefix={`translate-verify-${current.id || idx}`}
              className="mb-3 -mt-1"
            />

            {tri && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="border border-zinc-800/60 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 flex items-center gap-1">
                    <Bot className="h-3 w-3" /> claude
                  </div>
                  <VerdictBadge v={tri.claude?.verdict} />
                  <div className="text-[10.5px] text-zinc-400 mt-1 leading-snug line-clamp-3">
                    {tri.claude?.reasoning}
                  </div>
                </div>
                <div className="border border-zinc-800/60 bg-black/40 p-2">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 flex items-center gap-1">
                    <Bot className="h-3 w-3" /> gemini
                  </div>
                  <VerdictBadge v={tri.gemini?.verdict} />
                  <div className="text-[10.5px] text-zinc-400 mt-1 leading-snug line-clamp-3">
                    {tri.gemini?.reasoning}
                  </div>
                </div>
              </div>
            )}

            {/* Grounding sources */}
            <div className="border border-zinc-800/60 bg-black/30 p-2 mb-3">
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1.5 flex items-center gap-1.5">
                <Globe2 className="h-3 w-3" /> grounding · {sources.length} source{sources.length === 1 ? "" : "s"}
                <button
                  type="button"
                  onClick={reground}
                  disabled={regrounding}
                  data-testid="queue-reground-btn"
                  className="ml-auto text-[9px] uppercase tracking-[0.25em] text-sky-300 hover:text-sky-200 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${regrounding ? "animate-spin" : ""}`} />
                  re-ground (G)
                </button>
              </div>
              {sources.length === 0 ? (
                <div className="text-[10px] text-zinc-600 italic font-mono">
                  {tri?.grounding?.error || "no live sources fetched"}
                </div>
              ) : (
                <ul className="space-y-0.5">
                  {sources.slice(0, 4).map((s, i) => (
                    <li key={i}>
                      <a href={s.url} target="_blank" rel="noreferrer noopener"
                         className="inline-flex items-center gap-1 text-[10px] text-amber-300/80 hover:text-amber-200 truncate">
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{s.title || s.url}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Atoms (compact) */}
            {activeAtoms.length > 0 && (
              <div className="border border-zinc-800/60 bg-black/30 p-2 space-y-1.5">
                <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> atoms · {activeAtoms.length}
                </div>
                {activeAtoms.map((a) => (
                  <div key={a.id} data-testid={`queue-atom-${a.id}`} className="flex items-center gap-2 text-[10.5px] font-mono">
                    <KindPill kind={a.kind} />
                    <VerdictBadge v={a.verdict} />
                    <span className="text-zinc-300 flex-1 truncate">{a.text}</span>
                    {a.kind === "verifiable" && (
                      <span className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => setAtom(a.id, "confirmed")}
                          data-testid={`queue-atom-confirm-${a.id}`}
                          className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] border font-mono ${
                            a.architect_verdict === "confirmed"
                              ? "border-emerald-500/80 text-emerald-200 bg-emerald-500/20"
                              : "border-zinc-700 text-zinc-500 hover:border-emerald-500/40 hover:text-emerald-300"
                          }`}
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={() => setAtom(a.id, "rejected")}
                          data-testid={`queue-atom-reject-${a.id}`}
                          className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] border font-mono ${
                            a.architect_verdict === "rejected"
                              ? "border-rose-500/80 text-rose-200 bg-rose-500/20"
                              : "border-zinc-700 text-zinc-500 hover:border-rose-500/40 hover:text-rose-300"
                          }`}
                        >
                          ✗
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </article>

          {/* Swipe action bar */}
          <div className="grid grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => handle("rejected")}
              disabled={busy}
              data-testid="queue-action-reject"
              className="py-3 border border-rose-500/40 bg-rose-500/[0.04] text-rose-200 hover:bg-rose-500/15 disabled:opacity-40 font-mono uppercase tracking-[0.25em] text-[10px] inline-flex flex-col items-center gap-1"
            >
              <X className="h-4 w-4" />
              reject
              <kbd className="text-[8px] text-zinc-500">←</kbd>
            </button>
            <button
              type="button"
              onClick={() => handle("opinion")}
              disabled={busy}
              data-testid="queue-action-opinion"
              className="py-3 border border-violet-500/40 bg-violet-500/[0.04] text-violet-200 hover:bg-violet-500/15 disabled:opacity-40 font-mono uppercase tracking-[0.25em] text-[10px] inline-flex flex-col items-center gap-1"
            >
              <HelpCircle className="h-4 w-4" />
              opinion
              <kbd className="text-[8px] text-zinc-500">↑</kbd>
            </button>
            <button
              type="button"
              onClick={() => handle("skip")}
              disabled={busy}
              data-testid="queue-action-skip"
              className="py-3 border border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800/60 disabled:opacity-40 font-mono uppercase tracking-[0.25em] text-[10px] inline-flex flex-col items-center gap-1"
            >
              <SkipForward className="h-4 w-4" />
              skip
              <kbd className="text-[8px] text-zinc-500">↓</kbd>
            </button>
            <button
              type="button"
              onClick={() => handle("confirmed")}
              disabled={busy}
              data-testid="queue-action-confirm"
              className="py-3 border border-emerald-500/60 bg-emerald-500/[0.04] text-emerald-200 hover:bg-emerald-500/15 disabled:opacity-40 font-mono uppercase tracking-[0.25em] text-[10px] inline-flex flex-col items-center gap-1"
            >
              <Check className="h-4 w-4" />
              confirm · mint
              <kbd className="text-[8px] text-zinc-500">→</kbd>
            </button>
          </div>

          <div className="text-center text-[9.5px] uppercase tracking-[0.3em] text-zinc-600 font-mono">
            shortcuts · ← reject · → confirm · ↑ opinion · ↓ skip · G re-ground · A confirm-all-atoms · R reject-all-atoms
          </div>
        </>
      )}
    </div>
  );
}
